import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { Cookie, Fingerprint, Globe, Smartphone } from 'lucide-react';

// =============================================================================
// AttributionIcon — shared v2 primitive for the attribution-method chip used
// across Transactions tables (V1 + V2), expanded rows, and any future
// attribution-aware surface.
//
// Maps each attribution method key to its Lucide icon + human tooltip:
//   COOKIE             → Cookie        (deterministic browser cookie / uuid match)
//   FINGERPRINT        → Fingerprint   (probabilistic user-agent + IP hash)
//   DEVICE_FINGERPRINT → Smartphone    (probabilistic device model + IP)
//   IP                 → Globe         (probabilistic IP-only match)
//
// Mapping was chosen intent-first (the icon visually suggests what the match
// mechanism looks at) rather than shape-first. Swapped from develop's 4 PNG
// sprites (cnnaid / cnnaip / cnnafingerprint / globephone) in the v2 facelift
// — see MEMORY → `project_attribution_icons.md` for the historical context.
// =============================================================================

const COOKIE_ENTRY = {
  Icon: Cookie,
  tip: 'Cookie Match — Deterministic match using browser based cookie / uuid match.',
};

// `COOKIES` (plural) is emitted by the Sign Ups backend; `COOKIE` (singular)
// by the Transactions backend. Both alias to the same icon + tooltip so
// either variant renders correctly without per-consumer normalization.
const ATTRIBUTION_MAP = {
  COOKIE: COOKIE_ENTRY,
  COOKIES: COOKIE_ENTRY,
  FINGERPRINT: {
    Icon: Fingerprint,
    tip:
      'User Match — Probabilistic match using user fingerprint. (User fingerprint: the combination of user agent and IP address to produce a unique hash.)',
  },
  DEVICE_FINGERPRINT: {
    Icon: Smartphone,
    tip:
      'Device Match — Probabilistic match using user device model information (Device model, OS, OS version) combined with IP address.',
  },
  IP: {
    Icon: Globe,
    tip: 'IP Match — Probabilistic match using IP address.',
  },
};

// Parse a V1-style attribution column value. The legacy column stores either
// a stringified JSON object or an object literal — e.g. `{"COOKIE": …}` —
// where the first key is the matched attribution method. Returns the method
// key (or `null` if the shape doesn't match anything we recognize).
//
// V2 consumers pass the method key directly to `<AttributionIcon method="…"/>`
// and don't need this helper. V1 consumers parsing legacy column data do.
export const resolveAttributionMethod = (raw) => {
  if (!raw) return null;
  const record = typeof raw === 'string' ? safeJsonParse(raw) : raw;
  if (!record || typeof record !== 'object') return null;
  const key = Object.keys(record)[0];
  return key && ATTRIBUTION_MAP[key] ? key : null;
};

const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch (err) {
    return null;
  }
};

const AttributionIcon = ({
  method = 'IP',
  size = 18,
  strokeWidth = 1.75,
  className,
}) => {
  const entry = ATTRIBUTION_MAP[method] || ATTRIBUTION_MAP.IP;
  const { Icon, tip } = entry;
  const extraClass = className ? ` ${className}` : '';
  return (
    <Tooltip overlayClassName="mjl-ds" title={tip}>
      <span
        className={`campaign-overview__attr-icon${extraClass}`}
        aria-label={method}
      >
        <Icon size={size} strokeWidth={strokeWidth} />
      </span>
    </Tooltip>
  );
};

AttributionIcon.propTypes = {
  method: PropTypes.oneOf(['COOKIE', 'COOKIES', 'FINGERPRINT', 'DEVICE_FINGERPRINT', 'IP']),
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  className: PropTypes.string,
};

export default AttributionIcon;
